import type { SnakeCase } from "type-fest";
import type { ZodType } from "zod";
import z from "zod";

import { dayjs, looseUuid } from "@thalamos/common";

import {
  GROUP_CATEGORIES,
  type GroupCategory,
} from "../assessments/authoring.js";
import {
  CALENDAR_DAY,
  CALENDAR_TIME_ZONE,
  isLaterDay,
  today,
} from "../assessments/calendarDay.js";
import {
  AssessmentIdSchema,
  AssessmentSchema,
  FinishedAssessmentSchema,
  type AssessmentIdUnbranded,
  type AssessmentUnbranded,
  type FinishedAssessmentUnbranded,
  type StartAssessmentRequestUnbranded,
} from "./assessment.js";
import {
  PersonInvolvementIdSchema,
  type PersonInvolvementIdUnbranded,
} from "./involvement.js";
import { DateOfBirthSchema, type DateOfBirthUnbranded } from "./person.js";
import {
  ContributorRelationSchema,
  JobTitleSchema,
  type ContributorRelationUnbranded,
  type JobTitleUnbranded,
} from "./standing.js";
import {
  EmailAddressSchema,
  UserIdSchema,
  type EmailAddressUnbranded,
  type UserIdUnbranded,
} from "./user.js";

export type GuestAccessLinkIdUnbranded = string;
export const GuestAccessLinkIdSchema =
  looseUuid().brand<"haven-common:GuestAccessLinkId">() satisfies ZodType<GuestAccessLinkIdUnbranded>;
/**
 * One guest access link: the means by which a guest reaches their one
 * involvement. See `docs/glossary/guest-access-link.md`.
 */
export type GuestAccessLinkId = z.infer<typeof GuestAccessLinkIdSchema>;

/**
 * What a guest was given access for, named for the act and what it is done
 * to, so a later purpose can sit beside these. Each has an information
 * governance wording of its own.
 */
export type GuestAccessPurpose =
  "read_shared_assessments" | "complete_assessment";

export const GuestAccessPurposeSchema = z.enum([
  "read_shared_assessments",
  "complete_assessment",
]) satisfies ZodType<GuestAccessPurpose & SnakeCase<GuestAccessPurpose>>;

/**
 * Where the frontend's page for opening a guest access link is. The link's
 * token follows in the fragment.
 */
export const guestAccessPath = "/guest";

/**
 * Where the server sends a guest who signs out. A guest signed in through
 * their link, not the identity provider, so the staff sign-in page is no way
 * back for them.
 */
export const guestSignedOutPath = `${guestAccessPath}/signed-out`;

/** Counted across the code and the date of birth together. */
export const wrongTriesBeforeLock = 5;

/** How long an emailed code may be entered after it was sent. */
export const guestAccessCodeLifetimeMinutes = 10;

/**
 * How long after one code a guest may ask for another. Each new code replaces
 * the last and sends an email, so asking again at once would only flood the
 * guest's mailbox and cancel the code they are about to type.
 */
export const guestAccessCodeResendAfterSeconds = 60;

/** A code as the guest types it: six digits, leading zeros kept. */
export const GuestAccessCodeSchema = z.string().regex(/^\d{6}$/);

/**
 * Where a link stands when it is opened.
 *
 * - `open`: it may be signed in through.
 * - `locked`: the last wrong try was used. A lock is final.
 * - `declined`: the guest said it was not meant for them.
 * - `sent`: the guest sent the assessment they were asked for.
 * - `ended`: somebody ended the guest's access.
 * - `expired`: the end date the guest was given has passed.
 * - `address_refused`: the address it was sent to is no longer on a domain the
 *   inviting organisation allows guests from.
 */
export type GuestAccessLinkState =
  | "open"
  | "locked"
  | "declined"
  | "sent"
  | "ended"
  | "expired"
  | "address_refused";

export const GuestAccessLinkStateSchema = z.enum([
  "open",
  "locked",
  "declined",
  "sent",
  "ended",
  "expired",
  "address_refused",
]) satisfies ZodType<GuestAccessLinkState>;

/** Opening a link: the token from the address the guest was emailed. */
export type OpenGuestAccessLinkRequestUnbranded = { token: string };

/**
 * What a guest sees on opening their link: where it stands, and who to ask
 * about it. The young person is named by their first name alone.
 * `expiresAt` is the end the guest was given, which an `expired` link names;
 * `null` where none was set.
 */
export type GuestAccessLinkOpenedUnbranded = {
  state: GuestAccessLinkState;
  purpose: GuestAccessPurpose;
  guestName: string;
  sharerName: string;
  organisationName: string;
  personFirstName: string;
  expiresAt: Date | null;
};

export const GuestAccessLinkOpenedSchema = z.object({
  state: GuestAccessLinkStateSchema,
  purpose: GuestAccessPurposeSchema,
  guestName: z.string(),
  sharerName: z.string(),
  organisationName: z.string(),
  personFirstName: z.string(),
  expiresAt: z.coerce.date().nullable(),
}) satisfies ZodType<GuestAccessLinkOpenedUnbranded>;

export type GuestAccessLinkOpened = z.infer<typeof GuestAccessLinkOpenedSchema>;

/** A code the guest was emailed, as they typed it. */
export type EnterGuestAccessCodeRequestUnbranded = { code: string };

/** The young person's date of birth, as the guest gave it. */
export type EnterGuestDateOfBirthRequestUnbranded = {
  dateOfBirth: DateOfBirthUnbranded;
};

export const EnterGuestDateOfBirthRequestSchema = z.object({
  dateOfBirth: DateOfBirthSchema,
}) satisfies ZodType<EnterGuestDateOfBirthRequestUnbranded>;

/**
 * How a code or a date of birth was taken.
 *
 * - `accepted`: right; the guest moves on to the next step.
 * - `wrong`: not right, and `triesLeft` more wrong tries are allowed.
 * - `expired`: the code had run out, or none was sent. Not a wrong try.
 * - `locked`: that was the last wrong try, and the link is now locked.
 */
export type GuestSignInStepOutcome =
  | { outcome: "accepted" }
  | { outcome: "wrong"; triesLeft: number }
  | { outcome: "expired" }
  | { outcome: "locked" };

const WrongGuestSignInStepSchema = z.object({
  outcome: z.literal("wrong"),
  triesLeft: z.number().int().nonnegative(),
});

export const GuestSignInStepOutcomeSchema = z.discriminatedUnion("outcome", [
  z.object({ outcome: z.literal("accepted") }),
  WrongGuestSignInStepSchema,
  z.object({ outcome: z.literal("expired") }),
  z.object({ outcome: z.literal("locked") }),
]) satisfies ZodType<GuestSignInStepOutcome>;

/**
 * What follows a right date of birth: the information governance wording to
 * accept, or nothing, because the guest has already accepted these words and
 * is now signed in.
 */
export type GuestSignInNextStep =
  | { next: "agreement"; wording: GuestAgreementWordingUnbranded }
  | { next: "signed_in" };

export type GuestDateOfBirthOutcome =
  | ({ outcome: "accepted" } & GuestSignInNextStep)
  | Exclude<GuestSignInStepOutcome, { outcome: "accepted" }>;

/** A wording to accept, and the digest the acceptance names it by. */
export type GuestAgreementWordingUnbranded = {
  purpose: GuestAccessPurpose;
  text: string;
  digest: string;
};

const GuestAgreementWordingSchema = z.object({
  purpose: GuestAccessPurposeSchema,
  text: z.string(),
  digest: z.string(),
}) satisfies ZodType<GuestAgreementWordingUnbranded>;

// Every case spelt out with its tags, because the type's intersection tags an
// accepted outcome twice, which one discriminator cannot tell apart.
export const GuestDateOfBirthOutcomeSchema = z.union([
  z.object({
    outcome: z.literal("accepted"),
    next: z.literal("agreement"),
    wording: GuestAgreementWordingSchema,
  }),
  z.object({ outcome: z.literal("accepted"), next: z.literal("signed_in") }),
  WrongGuestSignInStepSchema,
  z.object({ outcome: z.literal("expired") }),
  z.object({ outcome: z.literal("locked") }),
]) satisfies ZodType<GuestDateOfBirthOutcome>;

/** Accepting the wording the guest was shown, named by its digest. */
export type AcceptGuestAgreementRequestUnbranded = { wordingDigest: string };

/** What a guest session acts in. */
export type GuestSessionUnbranded = {
  guestName: string;
  purpose: GuestAccessPurpose;
  sharerName: string;
  organisationName: string;
  personFirstName: string;
};

export const GuestSessionSchema = z.object({
  guestName: z.string(),
  purpose: GuestAccessPurposeSchema,
  sharerName: z.string(),
  organisationName: z.string(),
  personFirstName: z.string(),
}) satisfies ZodType<GuestSessionUnbranded>;

export type GuestSession = z.infer<typeof GuestSessionSchema>;

/**
 * How long a guest asked to complete an assessment has by default, in months:
 * the product's choice. The server takes whichever day the involver sends.
 */
export const askedGuestAccessMonths = 1;

/** The day a guest asked now has until, unless the involver chooses another. */
export const askedGuestEndsOnByDefault = (now: Date = new Date()): string =>
  dayjs(now)
    .tz(CALENDAR_TIME_ZONE)
    .add(askedGuestAccessMonths, "month")
    .format(CALENDAR_DAY);

/**
 * Involving somebody outside every organisation as an assessor of a person, to
 * complete one assessment.
 *
 * `email` is where the link is sent; a guest is involved even where it is an
 * account holder's address. `earlierGuestUserId` is an earlier guest the
 * involver confirmed is the one being involved, from those
 * `matchEarlierGuests` offered for this address; `null` involves somebody new
 * by `name`.
 *
 * `endsOn` is the UK calendar day the guest has until, today or later; access
 * ends at that day's end. `instrument` is the version to complete. `message`
 * is the involver's own words to the guest, or `null`.
 */
export type InvolveGuestAsAssessorRequestUnbranded = {
  name: string;
  email: EmailAddressUnbranded;
  earlierGuestUserId: UserIdUnbranded | null;
  relation: ContributorRelationUnbranded;
  jobTitle: JobTitleUnbranded;
  message: string | null;
  endsOn: string;
  instrument: StartAssessmentRequestUnbranded["instrument"];
};

/** The name a guest is asked or shared with by. */
const guestNameSchema = z
  .string()
  .trim()
  .min(1, "guest.nameRequired")
  .max(200, "guest.nameTooLong");

/** The UK calendar day a guest's access runs to the end of: today or later. */
const guestEndsOnSchema = z.iso
  .date({ error: "guest.endsOnInvalid", abort: true })
  .refine((day) => !isLaterDay(today(), day), "guest.endsOnPast");

// Long enough for a note of a few paragraphs, short enough that it stays a
// note to the guest rather than a report about the child.
const askMessageMaxLength = 2000;

export const InvolveGuestAsAssessorRequestSchema = z
  .object({
    name: guestNameSchema,
    email: EmailAddressSchema,
    earlierGuestUserId: UserIdSchema.nullable(),
    relation: ContributorRelationSchema,
    jobTitle: JobTitleSchema,
    // Blank is no message at all, so it is held as none.
    message: z
      .string()
      .trim()
      .max(askMessageMaxLength, "guest.messageTooLong")
      .transform((message) => (message === "" ? null : message))
      .nullable(),
    endsOn: guestEndsOnSchema,
    instrument: z.string().min(1, "assessment.instrument.required"),
  })
  .brand<"haven-common:InvolveGuestAsAssessorRequest">() satisfies ZodType<InvolveGuestAsAssessorRequestUnbranded>;

export type InvolveGuestAsAssessorRequest = z.infer<
  typeof InvolveGuestAsAssessorRequestSchema
>;

/** What involving a guest as an assessor started: their involvement and the assessment. */
export type GuestInvolvedAsAssessorUnbranded = {
  involvementId: PersonInvolvementIdUnbranded;
  assessmentId: AssessmentIdUnbranded;
};

export const GuestInvolvedAsAssessorSchema = z.object({
  involvementId: PersonInvolvementIdSchema,
  assessmentId: AssessmentIdSchema,
}) satisfies ZodType<GuestInvolvedAsAssessorUnbranded>;

export type GuestInvolvedAsAssessor = z.infer<
  typeof GuestInvolvedAsAssessorSchema
>;

/**
 * How long a guest assessments are shared with may read them by default, in
 * months: the product's choice. The server takes whichever day the sharer
 * sends.
 */
export const sharedGuestAccessMonths = 3;

/** The day a guest shared with now may read until, unless the sharer chooses another. */
export const sharedGuestEndsOnByDefault = (now: Date = new Date()): string =>
  dayjs(now)
    .tz(CALENDAR_TIME_ZONE)
    .add(sharedGuestAccessMonths, "month")
    .format(CALENDAR_DAY);

/**
 * Sharing finished assessments of a person with somebody outside every
 * organisation, to read until the end of a day.
 *
 * `assessmentIds` are the assessments shared, at least one. Who is shared with
 * is found as for `InvolveGuestAsAssessorRequestUnbranded`: `email` is where
 * the link is sent, and `earlierGuestUserId` is an earlier guest the sharer
 * confirmed, or `null` for somebody new by `name`. `endsOn` is the UK calendar
 * day the guest may read until, today or later.
 */
export type InvolveGuestAsSharedAssessmentReaderRequestUnbranded = {
  assessmentIds: AssessmentIdUnbranded[];
  name: string;
  email: EmailAddressUnbranded;
  earlierGuestUserId: UserIdUnbranded | null;
  relation: ContributorRelationUnbranded;
  jobTitle: JobTitleUnbranded;
  endsOn: string;
};

export const InvolveGuestAsSharedAssessmentReaderRequestSchema = z
  .object({
    assessmentIds: z
      .array(AssessmentIdSchema)
      .min(1, "guest.share.assessmentsRequired")
      .refine(
        (ids) => new Set(ids).size === ids.length,
        "guest.share.assessmentsRepeated",
      ),
    name: guestNameSchema,
    email: EmailAddressSchema,
    earlierGuestUserId: UserIdSchema.nullable(),
    relation: ContributorRelationSchema,
    jobTitle: JobTitleSchema,
    endsOn: guestEndsOnSchema,
  })
  .brand<"haven-common:InvolveGuestAsSharedAssessmentReaderRequest">() satisfies ZodType<InvolveGuestAsSharedAssessmentReaderRequestUnbranded>;

export type InvolveGuestAsSharedAssessmentReaderRequest = z.infer<
  typeof InvolveGuestAsSharedAssessmentReaderRequestSchema
>;

/** What sharing assessments with a guest started: their involvement. */
export type GuestInvolvedAsSharedAssessmentReaderUnbranded = {
  involvementId: PersonInvolvementIdUnbranded;
};

export const GuestInvolvedAsSharedAssessmentReaderSchema = z.object({
  involvementId: PersonInvolvementIdSchema,
}) satisfies ZodType<GuestInvolvedAsSharedAssessmentReaderUnbranded>;

export type GuestInvolvedAsSharedAssessmentReader = z.infer<
  typeof GuestInvolvedAsSharedAssessmentReaderSchema
>;

/** The address somebody is about to involve as a guest. */
export type MatchEarlierGuestsRequestUnbranded = {
  email: EmailAddressUnbranded;
};

export const MatchEarlierGuestsRequestSchema = z
  .object({ email: EmailAddressSchema })
  .brand<"haven-common:MatchEarlierGuestsRequest">() satisfies ZodType<MatchEarlierGuestsRequestUnbranded>;

export type MatchEarlierGuestsRequest = z.infer<
  typeof MatchEarlierGuestsRequestSchema
>;

/**
 * A guest an address was used for before, on a person the involver reaches
 * now, offered so the involver can say whether this is the guest they mean. The job
 * title is the one their latest involvement records.
 */
export type EarlierGuestUnbranded = {
  userId: UserIdUnbranded;
  name: string;
  jobTitle: JobTitleUnbranded | null;
};

export type EarlierGuestsUnbranded = { earlierGuests: EarlierGuestUnbranded[] };

export const EarlierGuestsSchema = z.object({
  earlierGuests: z.array(
    z.object({
      userId: UserIdSchema,
      name: z.string(),
      jobTitle: JobTitleSchema.nullable(),
    }),
  ),
}) satisfies ZodType<EarlierGuestsUnbranded>;

/** The earlier guests an address reached, as the involver is offered them. */
export type EarlierGuests = z.infer<typeof EarlierGuestsSchema>;

/** One earlier guest, as the involver is offered them. */
export type EarlierGuest = EarlierGuests["earlierGuests"][number];

/**
 * The assessment a guest session was asked to complete, with the asker's
 * message and the end of the day the guest has until.
 */
export type GuestAssessmentUnbranded = {
  assessment: AssessmentUnbranded;
  message: string | null;
  expiresAt: Date | null;
};

export const GuestAssessmentSchema = z.object({
  assessment: AssessmentSchema,
  message: z.string().nullable(),
  expiresAt: z.coerce.date().nullable(),
}) satisfies ZodType<GuestAssessmentUnbranded>;

/** The assessment a guest session was asked to complete, as the page reads it. */
export type GuestAssessment = z.infer<typeof GuestAssessmentSchema>;

/**
 * How many items of one kind of domain call for action in an assessment, as
 * its summary shows them (`summaryOf`): the headline of an assessment shared
 * with a guest.
 */
export type HeadlineCountUnbranded = {
  category: GroupCategory;
  count: number;
};

export const HeadlineCountSchema = z.object({
  category: z.enum(GROUP_CATEGORIES),
  count: z.number().int().nonnegative(),
}) satisfies ZodType<HeadlineCountUnbranded>;

export type HeadlineCount = z.infer<typeof HeadlineCountSchema>;

/**
 * One assessment shared with a guest, as their list shows it: what it was and
 * when it finished, whose work it is, and its headline counts, which are
 * `null` where its instrument says no kind of domain for any item. The
 * assessor's name is `null` where no involvement with the person names them.
 */
export type SharedAssessmentListingUnbranded = {
  assessment: FinishedAssessmentUnbranded;
  assessorName: string | null;
  headline: HeadlineCountUnbranded[] | null;
};

/**
 * What a guest session's link shares: who shared it and for which
 * organisation, the person by first name, the instant access ends, or
 * `null` where it has no end, and each assessment shared that the guest may
 * still read, most recently finished first.
 */
export type SharedWithGuestUnbranded = {
  sharerName: string;
  organisationName: string;
  personFirstName: string;
  expiresAt: Date | null;
  assessments: SharedAssessmentListingUnbranded[];
};

export const SharedWithGuestSchema = z.object({
  sharerName: z.string(),
  organisationName: z.string(),
  personFirstName: z.string(),
  expiresAt: z.coerce.date().nullable(),
  assessments: z.array(
    z.object({
      assessment: FinishedAssessmentSchema,
      assessorName: z.string().nullable(),
      headline: z.array(HeadlineCountSchema).nullable(),
    }),
  ),
}) satisfies ZodType<SharedWithGuestUnbranded>;

export type SharedWithGuest = z.infer<typeof SharedWithGuestSchema>;

/** One assessment shared with a guest, as their list reads it. */
export type SharedAssessmentListing = SharedWithGuest["assessments"][number];

/** One assessment shared with a guest, as they open it to read. */
export type SharedAssessmentUnbranded = { assessment: AssessmentUnbranded };

export const SharedAssessmentSchema = z.object({
  assessment: AssessmentSchema,
}) satisfies ZodType<SharedAssessmentUnbranded>;

export type SharedAssessment = z.infer<typeof SharedAssessmentSchema>;
